/**
 * MobileCategoryChips — navigation de la boutique sur téléphone.
 *
 * 2e version (26/09/2026, inspirée d'eFarmz) : un seul bouton FLOTTANT, en bas
 * sous le pouce, ouvre un panneau qui réunit les filtres (en tête) et les
 * rayons. La bande de pastilles du haut a disparu : en-tête, bandeau bio,
 * liens d'aide et pastilles prenaient près de 45 % de l'écran avant le premier
 * produit. Seule reste, en haut, la ligne des sous-rayons du rayon courant.
 *
 * Refonte du 26/09/2026. Les rayons vivaient au bout d'une bande de pastilles
 * défilante : sur un téléphone on en voit deux ou trois, et quatre filtres plus
 * « Tout » passaient devant — pour atteindre « Légumes » il fallait faire
 * glisser plusieurs écrans sans savoir ce qu'il y avait au bout. Désormais :
 *  - un bouton « Rayons » en tête de barre dit où l'on est et ouvre un
 *    PANNEAU : tous les rayons en tuiles sur un écran (icône, nom, nombre),
 *    et les sous-rayons d'un rayon en liste ;
 *  - les filtres restent à côté, à un doigt ;
 *  - la ligne des sous-rayons montre enfin celui qui est choisi, avec le
 *    nombre de produits, et masque ceux qui sont vides.
 * Un menu déroulant natif a été écarté : sur iPhone c'est une roue d'une ligne,
 * sans icône, sans nombre, et deux niveaux imposeraient deux menus à la suite.
 */

import { useRef, useEffect, useState, type ReactNode } from 'react';
import type { Category } from '../../../types/ecommerce.types';
import { rayonsVisibles, sousRayonsVisibles } from './rayonsMobiles';

interface MobileCategoryChipsProps {
  categories: Category[];
  selectedCategoryId: string | null;
  selectedSubCategoryId?: string | null;
  onSelectCategory: (categoryId: string | null) => void;
  onSelectSubCategory: (subCategoryId: string | null) => void;
  productCounts: Record<string, number>;
  /** Filtre « seulement ce qui vient de nos cultures ». */
  ownProductionOnly: boolean;
  onToggleOwnProduction: (value: boolean) => void;
  ownProductionCount: number;
  promoOnly: boolean;
  onTogglePromo: (checked: boolean) => void;
  promoCount: number;
  /** Favoris : chip montre SEULEMENT a un client connecte. */
  favorisOnly?: boolean;
  onToggleFavoris?: (v: boolean) => void;
  favorisCount?: number;
  noveltyOnly: boolean;
  onToggleNovelty: (v: boolean) => void;
  noveltyCount: number;
  /** Nombre de produits affichés avec les choix actuels (bouton « Voir les N produits »). */
  resultCount: number;
}

export default function MobileCategoryChips({
  categories,
  selectedCategoryId,
  selectedSubCategoryId = null,
  onSelectCategory,
  onSelectSubCategory,
  productCounts,
  ownProductionOnly,
  onToggleOwnProduction,
  ownProductionCount,
  promoOnly,
  onTogglePromo,
  promoCount,
  favorisOnly,
  onToggleFavoris,
  favorisCount,
  noveltyOnly,
  onToggleNovelty,
  noveltyCount,
  resultCount,
}: MobileCategoryChipsProps) {
  const [panneauOuvert, setPanneauOuvert] = useState(false);
  // Rayon dont on consulte les sous-rayons dans le panneau (null = la grille).
  const [rayonDeplie, setRayonDeplie] = useState<string | null>(null);
  const boutonRayonsRef = useRef<HTMLButtonElement>(null);
  const boutonFermerRef = useRef<HTMLButtonElement>(null);

  const rayons = rayonsVisibles(categories, productCounts, selectedCategoryId);
  const totalMagasin = Object.entries(productCounts)
    .filter(([id]) => categories.some((c) => c.id === id && !c.parent_id))
    .reduce((s, [, n]) => s + n, 0);
  const rayonCourant = categories.find((c) => c.id === selectedCategoryId) || null;
  const sousRayonCourant = categories.find((c) => c.id === selectedSubCategoryId) || null;
  const sousRayonsDuCourant = selectedCategoryId
    ? sousRayonsVisibles(categories, selectedCategoryId, productCounts, selectedSubCategoryId)
    : [];

  const filtresActifs = [promoOnly, noveltyOnly, ownProductionOnly, !!favorisOnly].filter(Boolean).length;

  const ouvrir = () => {
    setRayonDeplie(null);
    setPanneauOuvert(true);
  };
  const fermer = () => {
    setPanneauOuvert(false);
    // Rendre le focus au bouton qui a ouvert le panneau (clavier, lecteur d'écran).
    requestAnimationFrame(() => boutonRayonsRef.current?.focus());
  };

  useEffect(() => {
    if (!panneauOuvert) return;
    boutonFermerRef.current?.focus();
    const surTouche = (e: KeyboardEvent) => {
      if (e.key === 'Escape') fermer();
    };
    window.addEventListener('keydown', surTouche);
    return () => window.removeEventListener('keydown', surTouche);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panneauOuvert]);

  // Un rayon ou un sous-rayon se choisit d'un geste : le panneau se ferme.
  // Les filtres, eux, se combinent : on les coche puis « Voir les N produits ».
  const choisirRayon = (categoryId: string | null) => {
    onSelectCategory(categoryId);
    onSelectSubCategory(null);
    fermer();
  };
  const choisirSousRayon = (rayonId: string, sousRayonId: string) => {
    onSelectCategory(rayonId);
    onSelectSubCategory(sousRayonId);
    fermer();
  };
  const toucherRayon = (rayon: Category) => {
    const sous = sousRayonsVisibles(categories, rayon.id, productCounts, selectedSubCategoryId);
    if (sous.length > 0) setRayonDeplie(rayon.id);
    else choisirRayon(rayon.id);
  };

  const libelleBouton = sousRayonCourant?.name || rayonCourant?.name || 'Rayons & filtres';
  const rayonOuvertDansPanneau = rayonDeplie ? categories.find((c) => c.id === rayonDeplie) || null : null;

  return (
    <div className="shop-sans">
      {/* Sous-rayons du rayon courant : celui qui est choisi se voit, les
          vides ne sont pas proposés. Passer de l'un à l'autre sans rouvrir le panneau. */}
      {selectedCategoryId && sousRayonsDuCourant.length > 0 && (
        <div className="flex items-center gap-1.5 px-3 py-2 overflow-x-auto scrollbar-hide bg-[#faf7f0] border-b border-black/[.08]">
          <button
            onClick={() => onSelectSubCategory(null)}
            aria-pressed={!selectedSubCategoryId}
            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap border ${
              !selectedSubCategoryId
                ? 'bg-[#4a4530] text-white border-[#4a4530]'
                : 'bg-white text-[#4a4530] border-black/[.12] active:bg-[#f0e9d8]'
            }`}
          >
            Tout le rayon
          </button>
          {sousRayonsDuCourant.map((sub) => {
            const actif = sub.id === selectedSubCategoryId;
            return (
              <button
                key={sub.id}
                onClick={() => onSelectSubCategory(sub.id)}
                aria-pressed={actif}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap border flex items-center gap-1 ${
                  actif
                    ? 'bg-[#4a4530] text-white border-[#4a4530]'
                    : 'bg-white text-[#4a4530] border-black/[.12] active:bg-[#f0e9d8]'
                }`}
              >
                <span>{sub.name}</span>
                <span className={actif ? 'text-white/70' : 'text-[#6b6550]'}>{productCounts[sub.id] || 0}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Le bouton flottant, sous le pouce, juste au-dessus de la barre du bas. */}
      {!panneauOuvert && (
        <button
          ref={boutonRayonsRef}
          onClick={ouvrir}
          aria-haspopup="dialog"
          aria-expanded={panneauOuvert}
          style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 76px)' }}
          className="fixed left-1/2 -translate-x-1/2 z-40 max-w-[88vw] min-h-[48px] pl-4 pr-3 rounded-full bg-[#4a5c3a] text-white text-[15px] font-bold shadow-[0_6px_20px_rgba(40,50,30,0.35)] flex items-center gap-2 active:bg-[#3d4d30]"
        >
          <span aria-hidden className="text-base leading-none">{rayonCourant?.icon || '☰'}</span>
          <span className="truncate">{libelleBouton}</span>
          {filtresActifs > 0 && (
            <span
              className="min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#f0e9d8] text-[#4a5c3a] text-xs font-bold flex items-center justify-center"
              aria-label={`${filtresActifs} filtre${filtresActifs > 1 ? 's' : ''} actif${filtresActifs > 1 ? 's' : ''}`}
            >
              {filtresActifs}
            </span>
          )}
          <span aria-hidden className="text-xs text-white/80">▴</span>
        </button>
      )}

      {/* Le panneau : filtres, puis rayons */}
      {panneauOuvert && (
        <div className="fixed inset-0 z-[60] flex flex-col justify-end" role="presentation">
          <button
            type="button"
            aria-label="Fermer"
            tabIndex={-1}
            onClick={fermer}
            className="absolute inset-0 bg-black/40"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="titre-panneau-rayons"
            className="relative w-full sm:max-w-xl sm:mx-auto bg-[#faf7f0] rounded-t-2xl shadow-2xl max-h-[88vh] flex flex-col animate-slide-up motion-reduce:animate-none"
          >
            <div className="flex items-center justify-between gap-2 px-4 pt-3 pb-2 border-b border-black/[.08] shrink-0">
              {rayonOuvertDansPanneau ? (
                <button
                  onClick={() => setRayonDeplie(null)}
                  className="min-h-[44px] -ml-1 px-1 text-sm font-semibold text-[#b45f2e] flex items-center gap-1"
                >
                  <span aria-hidden>‹</span> Rayons
                </button>
              ) : (
                <span className="w-1" />
              )}
              <h2 id="titre-panneau-rayons" className="text-base font-bold text-[#3a3324] truncate">
                {rayonOuvertDansPanneau ? rayonOuvertDansPanneau.name : 'Rayons & filtres'}
              </h2>
              <button
                ref={boutonFermerRef}
                onClick={fermer}
                aria-label="Fermer"
                className="w-11 h-11 -mr-2 flex items-center justify-center rounded-full text-xl text-[#4a4530] active:bg-black/[.06]"
              >
                ✕
              </button>
            </div>

            <div className="overflow-y-auto px-3 pt-3 pb-4">
              {!rayonOuvertDansPanneau ? (
                <>
                  {/* Filtres : ils traversent les rayons, d'où leur place en tête. */}
                  <div className="flex flex-col gap-1.5 mb-4">
                    {promoCount > 0 && (
                      <LigneFiltre
                        icone={<span aria-hidden>🏷️</span>}
                        nom="Anti-gaspi"
                        nombre={promoCount}
                        actif={promoOnly}
                        onToggle={() => onTogglePromo(!promoOnly)}
                      />
                    )}
                    {noveltyCount > 0 && (
                      <LigneFiltre
                        icone={<span aria-hidden>✨</span>}
                        nom="Nouveautés"
                        nombre={noveltyCount}
                        actif={noveltyOnly}
                        onToggle={() => onToggleNovelty(!noveltyOnly)}
                      />
                    )}
                    {ownProductionCount > 0 && (
                      <LigneFiltre
                        icone={<img src="/logo-folies-badge.png" alt="" className="w-5 h-5 rounded-full bg-white object-contain" />}
                        nom="Notre production"
                        nombre={ownProductionCount}
                        actif={ownProductionOnly}
                        onToggle={() => onToggleOwnProduction(!ownProductionOnly)}
                      />
                    )}
                    {onToggleFavoris && (
                      <LigneFiltre
                        icone={<span aria-hidden>❤️</span>}
                        nom="Mes favoris"
                        nombre={favorisCount ?? 0}
                        actif={!!favorisOnly}
                        onToggle={() => onToggleFavoris(!favorisOnly)}
                      />
                    )}
                  </div>

                  <h3 className="px-1 mb-2 text-xs font-bold uppercase tracking-[.06em] text-[#6b6550]">Rayons</h3>
                  <div className="grid grid-cols-3 gap-2">
                    <TuileRayon
                      icone="🧺"
                      nom="Tout le magasin"
                      nombre={totalMagasin}
                      actif={!selectedCategoryId}
                      onClick={() => choisirRayon(null)}
                    />
                    {rayons.map((rayon) => {
                      const aDesSousRayons =
                        sousRayonsVisibles(categories, rayon.id, productCounts, selectedSubCategoryId).length > 0;
                      return (
                        <TuileRayon
                          key={rayon.id}
                          icone={rayon.icon || '🛒'}
                          nom={rayon.name}
                          nombre={productCounts[rayon.id] || 0}
                          actif={rayon.id === selectedCategoryId}
                          deplie={aDesSousRayons}
                          onClick={() => toucherRayon(rayon)}
                        />
                      );
                    })}
                  </div>
                </>
              ) : (
                <ul className="flex flex-col gap-1.5">
                  <li>
                    <LigneSousRayon
                      nom="Tout le rayon"
                      nombre={productCounts[rayonOuvertDansPanneau.id] || 0}
                      actif={selectedCategoryId === rayonOuvertDansPanneau.id && !selectedSubCategoryId}
                      fort
                      onClick={() => choisirRayon(rayonOuvertDansPanneau.id)}
                    />
                  </li>
                  {sousRayonsVisibles(categories, rayonOuvertDansPanneau.id, productCounts, selectedSubCategoryId).map(
                    (sub) => (
                      <li key={sub.id}>
                        <LigneSousRayon
                          nom={sub.name}
                          nombre={productCounts[sub.id] || 0}
                          actif={sub.id === selectedSubCategoryId}
                          onClick={() => choisirSousRayon(rayonOuvertDansPanneau.id, sub.id)}
                        />
                      </li>
                    ),
                  )}
                </ul>
              )}
            </div>

            <div className="shrink-0 px-3 pt-2 pb-3 border-t border-black/[.08] safe-area-bottom">
              <button
                onClick={fermer}
                className="w-full min-h-[50px] rounded-xl bg-[#4a5c3a] text-white text-base font-bold active:bg-[#3d4d30]"
              >
                {resultCount > 0
                  ? `Voir ${resultCount === 1 ? 'le produit' : `les ${resultCount} produits`}`
                  : 'Aucun produit — modifier les filtres'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LigneFiltre({
  icone,
  nom,
  nombre,
  actif,
  onToggle,
}: {
  icone: ReactNode;
  nom: string;
  nombre: number;
  actif: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      role="switch"
      aria-checked={actif}
      onClick={onToggle}
      className={`w-full min-h-[48px] px-4 rounded-xl flex items-center gap-3 text-left border ${
        actif ? 'bg-[#eef2e8] border-[#4a5c3a]/50' : 'bg-white border-black/[.08] active:bg-[#f0e9d8]'
      }`}
    >
      <span className="w-6 flex justify-center text-lg">{icone}</span>
      <span className="flex-1 text-[15px] font-semibold text-[#3a3324]">{nom}</span>
      <span className="text-sm tabular-nums text-[#6b6550]">{nombre}</span>
      <span
        aria-hidden
        className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${actif ? 'bg-[#4a5c3a]' : 'bg-black/[.18]'}`}
      >
        <span
          className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${actif ? 'left-[22px]' : 'left-0.5'}`}
        />
      </span>
    </button>
  );
}

function TuileRayon({
  icone,
  nom,
  nombre,
  actif,
  deplie,
  onClick,
}: {
  icone: string;
  nom: string;
  nombre: number;
  actif: boolean;
  deplie?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={actif}
      className={`min-h-[92px] rounded-xl px-1.5 py-2 flex flex-col items-center justify-center gap-1 text-center border transition-colors ${
        actif
          ? 'bg-[#4a5c3a] text-white border-[#4a5c3a]'
          : 'bg-white text-[#3a3324] border-black/[.08] active:bg-[#f0e9d8]'
      }`}
    >
      <span aria-hidden className="text-2xl leading-none">{icone}</span>
      <span className="text-[13px] font-semibold leading-tight line-clamp-2">{nom}</span>
      <span className={`text-xs tabular-nums ${actif ? 'text-white/75' : 'text-[#6b6550]'}`}>
        {nombre}
        {deplie && <span aria-hidden> ›</span>}
      </span>
    </button>
  );
}

function LigneSousRayon({
  nom,
  nombre,
  actif,
  fort,
  onClick,
}: {
  nom: string;
  nombre: number;
  actif: boolean;
  fort?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={actif}
      className={`w-full min-h-[48px] px-4 rounded-xl flex items-center justify-between gap-3 text-left border ${
        actif
          ? 'bg-[#4a5c3a] text-white border-[#4a5c3a]'
          : 'bg-white text-[#3a3324] border-black/[.08] active:bg-[#f0e9d8]'
      } ${fort ? 'font-bold' : 'font-medium'}`}
    >
      <span className="text-[15px]">{nom}</span>
      <span className={`text-sm tabular-nums ${actif ? 'text-white/75' : 'text-[#6b6550]'}`}>{nombre}</span>
    </button>
  );
}
